import { api } from '../../lib/apiClient';
import { uploadDirect } from '../../lib/directUpload';

/**
 * API surface for a file you have standing access to via an email-share grant
 * but are NOT a workspace member of — hits `shared/files/...` directly (no
 * `ws()` prefix, since there is no active workspace for these files). The
 * default `api` client already attaches the bearer token to every request
 * regardless of workspace, so this works exactly like `filesApi.js` otherwise.
 */

export const sharedKeys = {
  meta: (fileId) => ['shared-file-meta', fileId],
  sharedWithMe: () => ['shared-with-me'],
  comments: (fileId) => ['shared-comments', fileId],
  thread: (fileId) => ['shared-chat-thread', fileId],
  messages: (threadId) => ['shared-chat-messages', threadId],
  unread: (fileId) => ['shared-chat-unread', fileId],
  folderMeta: (folderId) => ['shared-folder-meta', folderId],
  sharedFoldersWithMe: () => ['shared-folders-with-me'],
  folderComments: (folderId) => ['shared-folder-comments', folderId],
  folderThread: (folderId) => ['shared-folder-chat-thread', folderId],
  folderMessages: (threadId) => ['shared-folder-chat-messages', threadId],
  folderUnread: (folderId) => ['shared-folder-chat-unread', folderId],
};

export async function listSharedWithMe() {
  const { data } = await api.get('/shared/files');
  return data;
}

export async function getSharedFileMeta(fileId) {
  const { data } = await api.get(`/shared/files/${fileId}/meta`);
  return data; // { file, actions, canDownload, canEdit, canChat }
}

export async function fetchSharedPreviewBlob(fileId) {
  const res = await api.get(`/shared/files/${fileId}/preview`, { responseType: 'blob' });
  return res.data;
}

export async function downloadSharedFile(fileId, name) {
  const res = await api.get(`/shared/files/${fileId}/content`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function uploadSharedFileVersion({ fileId, file, onProgress, signal }) {
  return uploadDirect({
    file,
    onProgress,
    signal,
    begin: async () => {
      const { data } = await api.post(`/shared/files/${fileId}/uploads`, {
        size: file.size,
        mimeType: file.type || undefined,
      });
      return data;
    },
  });
}

// ---- Chat ----
export async function resolveSharedChatThread(fileId) {
  const { data } = await api.post(`/shared/files/${fileId}/chat/thread`);
  return data;
}
export async function listSharedChatMessages(fileId, { page = 1, limit = 30 } = {}) {
  const { data } = await api.get(`/shared/files/${fileId}/chat/messages`, {
    params: { page, limit },
  });
  return data;
}
export async function postSharedChatMessage(fileId, body) {
  const { data } = await api.post(`/shared/files/${fileId}/chat/messages`, { body });
  return data;
}
export async function sharedChatUnreadCount(fileId) {
  const { data } = await api.get(`/shared/files/${fileId}/chat/unread-count`);
  return data.count;
}
export const markSharedThreadRead = (fileId, lastReadMessageId) =>
  api.post(`/shared/files/${fileId}/chat/read`, lastReadMessageId ? { lastReadMessageId } : {});

/** Same dedupe-and-prepend logic as `chatApi.insertChatMessage`, keyed under `sharedKeys`. */
export function insertSharedChatMessage(queryClient, threadId, message) {
  queryClient.setQueryData(sharedKeys.messages(threadId), (old) => {
    if (!old?.pages?.length) return old;
    if (old.pages.some((p) => p.data.some((m) => m.id === message.id))) return old;
    const [first, ...rest] = old.pages;
    return { ...old, pages: [{ ...first, data: [message, ...first.data] }, ...rest] };
  });
}

// ---- Comments ----
export async function listSharedComments(fileId) {
  const { data } = await api.get(`/shared/files/${fileId}/comments`);
  return data;
}
export async function createSharedComment({ fileId, body, parentCommentId }) {
  const { data } = await api.post(`/shared/files/${fileId}/comments`, { body, parentCommentId });
  return data;
}

// ---- Folders (same grant model, scoped to `shared/folders/...`) ----

export async function listSharedFoldersWithMe() {
  const { data } = await api.get('/shared/folders');
  return data;
}

export async function getSharedFolderMeta(folderId) {
  const { data } = await api.get(`/shared/folders/${folderId}/meta`);
  return data; // { folder, folders, files, actions, canDownload, canEdit, canChat }
}

export async function downloadSharedFolderFile(folderId, fileId, name) {
  const res = await api.get(`/shared/folders/${folderId}/files/${fileId}/content`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// ---- Folder chat ----
export async function resolveSharedFolderChatThread(folderId) {
  const { data } = await api.post(`/shared/folders/${folderId}/chat/thread`);
  return data;
}
export async function listSharedFolderChatMessages(folderId, { page = 1, limit = 30 } = {}) {
  const { data } = await api.get(`/shared/folders/${folderId}/chat/messages`, {
    params: { page, limit },
  });
  return data;
}
export async function postSharedFolderChatMessage(folderId, body) {
  const { data } = await api.post(`/shared/folders/${folderId}/chat/messages`, { body });
  return data;
}
export async function sharedFolderChatUnreadCount(folderId) {
  const { data } = await api.get(`/shared/folders/${folderId}/chat/unread-count`);
  return data.count;
}
export const markSharedFolderThreadRead = (folderId, lastReadMessageId) =>
  api.post(`/shared/folders/${folderId}/chat/read`, lastReadMessageId ? { lastReadMessageId } : {});
export function insertSharedFolderChatMessage(queryClient, threadId, message) {
  queryClient.setQueryData(sharedKeys.folderMessages(threadId), (old) => {
    if (!old?.pages?.length) return old;
    if (old.pages.some((p) => p.data.some((m) => m.id === message.id))) return old;
    const [first, ...rest] = old.pages;
    return { ...old, pages: [{ ...first, data: [message, ...first.data] }, ...rest] };
  });
}

// ---- Folder comments ----
export async function listSharedFolderComments(folderId) {
  const { data } = await api.get(`/shared/folders/${folderId}/comments`);
  return data;
}
export async function createSharedFolderComment({ folderId, body, parentCommentId }) {
  const { data } = await api.post(`/shared/folders/${folderId}/comments`, {
    body,
    parentCommentId,
  });
  return data;
}
